import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  rgbAtPercent,
  C2M_CANVAS,
  C2M_LEGEND,
  C2M_ARROW,
  drawC2MLegend,
  drawScanlineArrow,
} from '../utils/c2mRender';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useAnalysisActions } from '../contexts/AnalysisActionsContext';
import { getClientById, getObjectById, getInspectionById } from '../utils/storage';
import { AngleInput, ProgressModal } from '../utils/defoCommon';
import { bundleFloorResults } from '../utils/defoExport';
import { post } from '../api';
import './ShellDeformationPage.css'; // reuse the .defo-* class set

// ── Auto-scanline-count rule (shared with the shell page) ──────────
// n = round(D / 3.05), forced even, ≥ 8, then bumped (by 2) until the
// chord spacing between adjacent scanlines on the tank circumference
// is ≤ 10 m.
function autoScanlineCount(diameterMm) {
  if (!diameterMm || diameterMm <= 0) return 8;
  const D_m = diameterMm / 1000;
  let n = Math.round(D_m / 3.05);
  if (n % 2 !== 0) n += 1;
  if (n < 8) n = 8;
  while ((Math.PI * D_m / n) > 10) n += 2;
  return n;
}

// ── Angle helpers ────────────────────────────────────────────────────
// Mirror of the shell page's helpers, plus a paired-angle rule that's
// floor-specific: scanline i (i < N/2) is editable, and scanline i+N/2
// is auto-locked at (angle_i + 180) mod 360.

function defaultAnglesFor(n) {
  if (n < 1) return [];
  const step = 360 / n;
  return Array.from({ length: n }, (_, i) => ((Math.round(i * step) % 360) + 360) % 360);
}

function angleBoundsFor(idx, n) {
  const step = 360 / n;
  const half = step / 2;
  const center = idx * step;
  const lowerRaw = center - half;
  const upperRaw = center + half;
  const lower = Math.ceil(lowerRaw);
  const upper = Math.floor(upperRaw);
  const lowerMod = ((lower % 360) + 360) % 360;
  const upperMod = ((upper % 360) + 360) % 360;
  const wraps = lower < 0 || upper > 359 || lowerMod > upperMod;
  return { lower: lowerMod, upper: upperMod, wraps };
}

function isValidAngle(val, idx, n) {
  if (!Number.isInteger(val)) return false;
  if (val < 0 || val > 359) return false;
  const { lower, upper, wraps } = angleBoundsFor(idx, n);
  return wraps ? (val >= lower || val <= upper) : (val >= lower && val <= upper);
}

// Gaussian elimination on a 3×3 system. Returns [x0, x1, x2] or null
// if the matrix is singular. Used by the polynomial floor-shape fit.
function solve3x3(M, Y) {
  const A = M.map((row) => row.slice());
  const b = Y.slice();
  for (let i = 0; i < 3; i++) {
    let pivot = i;
    for (let k = i + 1; k < 3; k++) if (Math.abs(A[k][i]) > Math.abs(A[pivot][i])) pivot = k;
    [A[i], A[pivot]] = [A[pivot], A[i]];
    [b[i], b[pivot]] = [b[pivot], b[i]];
    if (Math.abs(A[i][i]) < 1e-15) return null;
    for (let k = i + 1; k < 3; k++) {
      const f = A[k][i] / A[i][i];
      for (let j = i; j < 3; j++) A[k][j] -= f * A[i][j];
      b[k] -= f * b[i];
    }
  }
  const x = [0, 0, 0];
  for (let i = 2; i >= 0; i--) {
    x[i] = b[i];
    for (let j = i + 1; j < 3; j++) x[i] -= A[i][j] * x[j];
    x[i] /= A[i][i];
  }
  return x;
}

// Floor-shape classifier. Walks the binned (R, θ, Z̄) cells, builds a
// Z̄(R) profile (Z averaged across θ at every R), then fits both a
// linear (Z = c1·R + c0) and a quadratic (Z = c2·R² + c1·R + c0) model.
// Classification (mm units, untilted Z assumed):
//   • SPHEROID when the quadratic R² improves on the linear R² by
//     more than 0.05 AND the curvature term |c2| is significant.
//   • CONE-UP / CONE-DOWN when the linear slope magnitude > 1 % / mm
//     of the tank radius (i.e. > 0.001 in mm/mm).
//   • FLAT otherwise.
function classifyFloorShape(cellsBuffer, nominalRMm, forceClassification = null) {
  if (!cellsBuffer) return null;
  const cells = new Float32Array(cellsBuffer);
  if (cells.length === 0) return null;

  // Group Z values by R_mm. Each R-bin then collapses to a single
  // representative Z via a ±1.5σ filtered mean (outlier rejection),
  // with a plain-mean fallback when the R-bin has too few cells for
  // a meaningful stdev.
  const zByR = new Map(); // R_mm → Array<Z>
  for (let i = 0; i < cells.length; i += 4) {
    const r = cells[i + 1];
    const z = cells[i + 2];
    let arr = zByR.get(r);
    if (!arr) { arr = []; zByR.set(r, arr); }
    arr.push(z);
  }
  const SIGMA_K = 1.5;
  const MIN_COUNT_FOR_FILTER = 8;
  const profile = [];
  let cellsAll = 0, cellsKept = 0;
  for (const [r, zs] of zByR) {
    cellsAll += zs.length;
    if (zs.length < MIN_COUNT_FOR_FILTER) {
      // Too few samples for a meaningful σ — use the plain mean.
      let s = 0; for (const z of zs) s += z;
      profile.push({ r, z: s / zs.length });
      cellsKept += zs.length;
      continue;
    }
    let s = 0; for (const z of zs) s += z;
    const mu = s / zs.length;
    let ssq = 0; for (const z of zs) { const d = z - mu; ssq += d * d; }
    const sigma = Math.sqrt(ssq / zs.length);
    const cutoff = SIGMA_K * sigma;
    let keptSum = 0, keptCount = 0;
    for (const z of zs) {
      if (Math.abs(z - mu) <= cutoff) { keptSum += z; keptCount++; }
    }
    const meanRobust = keptCount > 0 ? keptSum / keptCount : mu;
    profile.push({ r, z: meanRobust });
    cellsKept += keptCount;
  }
  profile.sort((a, b) => a.r - b.r);
  if (profile.length < 4) return null;

  // Normalise R to [0, 1] using nominal R to keep the normal-equations
  // matrix well-conditioned. We'll convert coefficients back to
  // absolute R afterwards.
  const Rscale = Math.max(1, nominalRMm);
  const n = profile.length;
  let sR = 0, sR2 = 0, sR3 = 0, sR4 = 0;
  let sZ = 0, sRZ = 0, sR2Z = 0;
  for (const p of profile) {
    const r = p.r / Rscale, z = p.z;
    sR  += r;     sR2 += r * r;
    sR3 += r * r * r;
    sR4 += r * r * r * r;
    sZ  += z;     sRZ += r * z;
    sR2Z += r * r * z;
  }
  const meanZ = sZ / n;
  let ssTot = 0;
  for (const p of profile) ssTot += (p.z - meanZ) * (p.z - meanZ);

  // Linear fit Z = a·(R/Rscale) + b
  const denomLin = n * sR2 - sR * sR;
  let aLin = 0, bLin = meanZ, rssLin = 0;
  if (Math.abs(denomLin) > 1e-15) {
    aLin = (n * sRZ - sR * sZ) / denomLin;
    bLin = (sZ - aLin * sR) / n;
    for (const p of profile) {
      const pred = aLin * (p.r / Rscale) + bLin;
      rssLin += (p.z - pred) * (p.z - pred);
    }
  } else {
    rssLin = ssTot;
  }
  const r2Lin = ssTot > 0 ? 1 - rssLin / ssTot : 0;

  // Quadratic fit Z = c2·(R/Rscale)² + c1·(R/Rscale) + c0
  const M = [
    [sR4, sR3, sR2],
    [sR3, sR2, sR],
    [sR2, sR,  n],
  ];
  const Yv = [sR2Z, sRZ, sZ];
  const coef = solve3x3(M, Yv);
  let c2 = 0, c1 = aLin, c0 = bLin, rssQuad = rssLin;
  if (coef) {
    c2 = coef[0]; c1 = coef[1]; c0 = coef[2];
    rssQuad = 0;
    for (const p of profile) {
      const rn = p.r / Rscale;
      const pred = c2 * rn * rn + c1 * rn + c0;
      rssQuad += (p.z - pred) * (p.z - pred);
    }
  }
  const r2Quad = ssTot > 0 ? 1 - rssQuad / ssTot : 0;

  // Convert normalised coefficients back to absolute-R form:
  //   linear:    a_abs = aLin / Rscale          (mm/mm slope)
  //   quadratic: c2_abs = c2 / Rscale²
  //              c1_abs = c1 / Rscale
  const slopeAbs    = aLin / Rscale;
  const c2_abs      = c2 / (Rscale * Rscale);
  const c1_abs      = c1 / Rscale;

  // Classification thresholds (calibrated for mm units).
  const SLOPE_THRESH = 1e-3;        // 0.1 % slope (1 mm rise per metre R)
  const R2_GAIN      = 0.05;        // quad must beat linear by 5 % of variance
  const C2_THRESH    = 1e-9;        // 1 mm curvature over 1 m R

  // Auto-detected classification (the "initial guess").
  let autoClassification;
  if (r2Quad > r2Lin + R2_GAIN && Math.abs(c2_abs) > C2_THRESH) {
    autoClassification = 'spheroid';
  } else if (Math.abs(slopeAbs) > SLOPE_THRESH) {
    // Positive slope ⇒ Z rises as R grows ⇒ rim is higher than centre
    //   ⇒ centre is "dished down" ⇒ CONE-DOWN.
    // Negative slope ⇒ centre is higher than rim ⇒ CONE-UP.
    autoClassification = slopeAbs > 0 ? 'cone-down' : 'cone-up';
  } else {
    autoClassification = 'flat';
  }

  // The forced classification (when the user overrides the dropdown
  // in the page) bypasses the auto-detect. Both fits — linear and
  // quadratic — and the piecewise spheroid segments below are still
  // computed regardless, so we can switch among models without
  // re-running anything else.
  const VALID = new Set(['flat', 'cone-up', 'cone-down', 'spheroid']);
  const classification =
    (forceClassification && VALID.has(forceClassification))
      ? forceClassification
      : autoClassification;

  // ── Piecewise quadratic over 1 m segments (spheroid only) ────────
  // A single global quadratic forces uniform curvature across all R,
  // which under-fits real dished/domed floors where the curvature is
  // concentrated near the rim. We segment R into 1 000 mm chunks and
  // fit each independently. The fits are not constrained to be
  // continuous at segment boundaries; the data they're computed from
  // is continuous so the seams are usually invisible.
  let segments = null;
  let r2Piecewise = null;
  if (classification === 'spheroid') {
    const SEG_MM = 1000;
    const rangeMin = profile[0].r;
    const rangeMax = profile[profile.length - 1].r;
    const startEdge = Math.floor(rangeMin / SEG_MM) * SEG_MM;
    segments = [];
    for (let segStart = startEdge; segStart < rangeMax; segStart += SEG_MM) {
      const segEnd = segStart + SEG_MM;
      const segPts = profile.filter((p) => p.r >= segStart && p.r < segEnd);
      if (segPts.length === 0) continue;

      let fit;
      if (segPts.length === 1) {
        fit = { type: 'const', value: segPts[0].z };
      } else if (segPts.length === 2) {
        const [p1, p2] = segPts;
        const a = (p2.z - p1.z) / Math.max(1, p2.r - p1.r);
        fit = { type: 'linear', a, b: p1.z - a * p1.r };
      } else {
        // Quadratic via normal equations on raw R (no normalisation —
        // each segment is at most 1 m wide so conditioning is fine).
        let sR = 0, sR2 = 0, sR3 = 0, sR4 = 0;
        let sZ = 0, sRZ = 0, sR2Z = 0;
        const ns = segPts.length;
        for (const p of segPts) {
          const r = p.r;
          sR  += r;          sR2 += r * r;
          sR3 += r * r * r;  sR4 += r * r * r * r;
          sZ  += p.z;
          sRZ += r * p.z;
          sR2Z += r * r * p.z;
        }
        const Msg = [
          [sR4, sR3, sR2],
          [sR3, sR2, sR],
          [sR2, sR,  ns],
        ];
        const Ysg = [sR2Z, sRZ, sZ];
        const cSeg = solve3x3(Msg, Ysg);
        if (cSeg) {
          fit = { type: 'quad', c2: cSeg[0], c1: cSeg[1], c0: cSeg[2] };
        } else {
          // Singular — fall back to constant.
          let s = 0; for (const p of segPts) s += p.z;
          fit = { type: 'const', value: s / ns };
        }
      }
      segments.push({ rMin: segStart, rMax: segEnd, fit, pointCount: segPts.length });
    }

    // Piecewise R² — how well the segmented model explains the variance.
    let rssSeg = 0;
    for (const p of profile) {
      const seg = segments.find((s) => p.r >= s.rMin && p.r < s.rMax) || segments[segments.length - 1];
      let pred;
      if (!seg) { pred = meanZ; }
      else if (seg.fit.type === 'quad')   pred = seg.fit.c2 * p.r * p.r + seg.fit.c1 * p.r + seg.fit.c0;
      else if (seg.fit.type === 'linear') pred = seg.fit.a  * p.r + seg.fit.b;
      else                                pred = seg.fit.value;
      rssSeg += (p.z - pred) * (p.z - pred);
    }
    r2Piecewise = ssTot > 0 ? 1 - rssSeg / ssTot : 0;
  }

  // Predictor for the cross-section overlay — uses the most-specific
  // model the classification supports.
  const predict = (() => {
    if (classification === 'spheroid' && segments && segments.length > 0) {
      return (rMm) => {
        let seg = segments.find((s) => rMm >= s.rMin && rMm < s.rMax);
        if (!seg) seg = rMm < segments[0].rMin ? segments[0] : segments[segments.length - 1];
        if (seg.fit.type === 'quad')   return seg.fit.c2 * rMm * rMm + seg.fit.c1 * rMm + seg.fit.c0;
        if (seg.fit.type === 'linear') return seg.fit.a  * rMm + seg.fit.b;
        return seg.fit.value;
      };
    }
    if (classification === 'flat') return () => meanZ;
    return (rMm) => aLin * (rMm / Rscale) + bLin;
  })();

  return {
    classification,
    autoClassification,
    isOverridden: classification !== autoClassification,
    profile,
    meanZ,
    linear:    { slope_mm_per_mm: slopeAbs, intercept_mm: bLin, r2: r2Lin },
    quadratic: { c2_per_mm2: c2_abs, c1_per_mm: c1_abs, c0_mm: c0, r2: r2Quad },
    segments,
    piecewiseR2: r2Piecewise,
    predict,
    filterStats: { cellsAll, cellsKept, sigmaK: SIGMA_K, minCountForFilter: MIN_COUNT_FOR_FILTER },
  };
}

// ── Niceness helper for chart ticks ────────────────────────────────
function niceTicks(min, max, target = 5) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return Number.isFinite(min) ? [min] : [];
  }
  const range = max - min;
  const rough = range / Math.max(1, target - 1);
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  let step;
  if (norm < 1.5) step = 1 * pow;
  else if (norm < 3) step = 2 * pow;
  else if (norm < 7) step = 5 * pow;
  else step = 10 * pow;
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + step * 1e-6; v += step) ticks.push(v);
  return ticks;
}

// ── Paired scanline R-vs-Z chart ───────────────────────────────────
// One chart per scanline pair (i, i + N/2). Two coloured polylines:
// scanline i in COLOR_A, its 180°-opposite in COLOR_B. The same two
// colours are reused for every plot in the grid.
const FLOOR_PAIR_COLOR_A = '#2563eb'; // blue — primary scanline (i)
const FLOOR_PAIR_COLOR_B = '#f97316'; // orange — opposite (i + N/2)

function ScanlinePairChart({
  pairIndex, totalScanlines,
  angleA, angleB, pointsByBin,
  binsPerCircle, thetaBinDeg, shape,
}) {
  // Landscape viewBox in the same small user-space scale the shell
  // scanline charts use. Extra bottom room (H 220, bottom margin 58)
  // holds a centered legend while keeping innerH the same as before.
  const W = 340, H = 220;
  const margins = { left: 50, right: 14, top: 14, bottom: 58 };
  const innerW = W - margins.left - margins.right;
  const innerH = H - margins.top - margins.bottom;
  const scanA = pairIndex + 1;
  const scanB = pairIndex + 1 + totalScanlines / 2;

  const binIdxFor = (angle) => {
    if (!Number.isFinite(angle)) return -1;
    const raw = Math.round(angle / thetaBinDeg) % binsPerCircle;
    return ((raw % binsPerCircle) + binsPerCircle) % binsPerCircle;
  };

  const binA = binIdxFor(angleA);
  const binB = binIdxFor(angleB);
  const ptsA = (binA >= 0 ? pointsByBin.get(binA) : null) || [];
  const ptsB = (binB >= 0 ? pointsByBin.get(binB) : null) || [];

  if (ptsA.length === 0 && ptsB.length === 0) {
    return (
      <div className="defo-chart">
        <div className="defo-chart-title">Scanline {scanA} ({angleA}°) - Scanline {scanB} ({angleB}°)</div>
        <div className="defo-chart-empty">No points in either θ-bin.</div>
      </div>
    );
  }

  // Plot diametrically:
  //   scanline A (the primary) at +R
  //   scanline B (its 180°-opposite partner) at −R
  // so the chart reads as a true diametric profile from −R through the
  // centre (x = 0) to +R.
  const plotA = ptsA.map((p) => ({ x:  p.r, z: p.z }));
  const plotB = ptsB.map((p) => ({ x: -p.r, z: p.z })).sort((a, b) => a.x - b.x);

  let xMin = Infinity, xMax = -Infinity, zMin = Infinity, zMax = -Infinity;
  for (const arr of [plotA, plotB]) for (const p of arr) {
    if (p.x < xMin) xMin = p.x; if (p.x > xMax) xMax = p.x;
    if (p.z < zMin) zMin = p.z; if (p.z > zMax) zMax = p.z;
  }
  // Symmetrise the X axis so x = 0 lands at the tank centre.
  const xAbs = Math.max(Math.abs(xMin), Math.abs(xMax)) || 1;
  xMin = -xAbs; xMax = xAbs;

  // Shared nominal curve from the fitted floor shape — Z depends on |R|,
  // so it's symmetric about the centre. Computed once, drawn on every pair.
  const NOM_PTS = 60;
  const nominalPts = [];
  if (shape && typeof shape.predict === 'function') {
    for (let i = 0; i <= NOM_PTS; i++) {
      const x = xMin + (xMax - xMin) * (i / NOM_PTS);
      const z = shape.predict(Math.abs(x));
      if (Number.isFinite(z)) {
        nominalPts.push({ x, z });
        if (z < zMin) zMin = z;
        if (z > zMax) zMax = z;
      }
    }
  }

  const zPad = (zMax - zMin) * 0.06 || 1;
  zMin -= zPad; zMax += zPad;

  const xScale = (x) => margins.left + ((x - xMin) / (xMax - xMin)) * innerW;
  const yScale = (z) => margins.top + innerH - ((z - zMin) / (zMax - zMin)) * innerH;

  const xTicks = niceTicks(xMin, xMax, 7);
  const zTicks = niceTicks(zMin, zMax, 5);

  const polylineFor = (arr) =>
    arr.map((p) => `${xScale(p.x).toFixed(1)},${yScale(p.z).toFixed(1)}`).join(' ');

  const legendY = margins.top + innerH + 34;   // below the x-axis label
  return (
    <div className="defo-chart">
      <div className="defo-chart-title">
        Scanline {scanA} ({angleA}°) - Scanline {scanB} ({angleB}°)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="defo-chart-svg" preserveAspectRatio="xMidYMid meet">
        <line x1={margins.left} y1={margins.top}
              x2={margins.left} y2={margins.top + innerH} className="defo-axis" />
        <line x1={margins.left} y1={margins.top + innerH}
              x2={margins.left + innerW} y2={margins.top + innerH} className="defo-axis" />

        {xTicks.map((t, i) => (
          <g key={`xt${i}`}>
            <line x1={xScale(t)} y1={margins.top + innerH}
                  x2={xScale(t)} y2={margins.top + innerH + 4} className="defo-axis" />
            <text x={xScale(t)} y={margins.top + innerH + 12}
                  textAnchor="middle" className="defo-tick-label">{Math.round(t)}</text>
          </g>
        ))}
        {zTicks.map((t, i) => (
          <g key={`yt${i}`}>
            <line x1={margins.left - 4} y1={yScale(t)}
                  x2={margins.left} y2={yScale(t)} className="defo-axis" />
            <text x={margins.left - 6} y={yScale(t) + 3}
                  textAnchor="end" className="defo-tick-label">{Math.round(t)}</text>
          </g>
        ))}

        {/* Faint vertical reference at x = 0 (tank centre). */}
        <line x1={xScale(0)} y1={margins.top}
              x2={xScale(0)} y2={margins.top + innerH}
              className="defo-axis" strokeDasharray="3 3" opacity={0.5} />

        <text x={margins.left + innerW / 2} y={H - 6}
              textAnchor="middle" className="defo-axis-label">R (mm)</text>
        <text x={14} y={margins.top + innerH / 2}
              textAnchor="middle" className="defo-axis-label"
              transform={`rotate(-90, 14, ${margins.top + innerH / 2})`}>Z (mm)</text>

        {plotB.length > 0 && (
          <polyline points={polylineFor(plotB)} fill="none"
            stroke={FLOOR_PAIR_COLOR_B} strokeWidth={1.4}
            strokeLinejoin="round" strokeLinecap="round" />
        )}
        {plotA.length > 0 && (
          <polyline points={polylineFor(plotA)} fill="none"
            stroke={FLOOR_PAIR_COLOR_A} strokeWidth={1.4}
            strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* Shared nominal (fitted floor shape) — red dashed, mirrors the shell
            profile nominal line. */}
        {nominalPts.length > 1 && (
          <polyline points={polylineFor(nominalPts)} fill="none"
            stroke="#dc2626" strokeWidth={1.2} strokeDasharray="4 3"
            strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* Legend — centered at the bottom of the plot. */}
        <g className="defo-tick-label" style={{ fontFamily: 'Inter, sans-serif' }}>
          <line x1={38} y1={legendY} x2={52} y2={legendY} stroke={FLOOR_PAIR_COLOR_A} strokeWidth={1.6} />
          <text x={55} y={legendY + 3} textAnchor="start">Scanline {scanA}</text>
          <line x1={140} y1={legendY} x2={154} y2={legendY} stroke={FLOOR_PAIR_COLOR_B} strokeWidth={1.6} />
          <text x={157} y={legendY + 3} textAnchor="start">Scanline {scanB}</text>
          <line x1={242} y1={legendY} x2={256} y2={legendY} stroke="#dc2626" strokeWidth={1.6} strokeDasharray="4 3" />
          <text x={259} y={legendY + 3} textAnchor="start">Nominal</text>
        </g>
      </svg>
    </div>
  );
}

// ── Floor-shape cross-section chart (Canvas) ───────────────────────
// All (R, Z) cells plotted as small blue dots. The detected shape
// model is overlaid as a red dashed line. Axes drawn manually because
// rendering 100 k+ dots in SVG would melt the browser.
function FloorShapeChart({ shape, cellsBuffer }) {
  const canvasRef = useRef(null);
  // Same small user-space scale + landscape aspect as the scanline
  // pair chart (340 × 200) — gives the canvas the same visual weight
  // for axis text and the fit line. We still draw at a 3× supersample
  // (1020 × 600 backing pixels) so the canvas stays crisp when CSS
  // scales it to the full card width.
  const VB_W = 340, VB_H = 200;
  const SCALE = 6;   // 2× the previous 3× supersample → crisper axes/labels
  const W = VB_W * SCALE, H = VB_H * SCALE;
  const margins = { left: 50 * SCALE, right: 14 * SCALE, top: 14 * SCALE, bottom: 38 * SCALE };
  const innerW = W - margins.left - margins.right;
  const innerH = H - margins.top - margins.bottom;

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setIsDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    if (!shape || !shape.profile || shape.profile.length === 0 || !cellsBuffer) return;

    const axisColor   = isDark ? '#cbd5e1' : '#4b5563';
    const labelColor  = isDark ? '#cbd5e1' : '#6b7280';
    const titleColor  = isDark ? '#f1f5f9' : '#374151';
    const dotColor    = isDark ? '#60a5fa' : '#2563eb';
    const fitColor    = '#dc2626';
    const segColor    = isDark ? '#475569' : '#cbd5e1';

    const cells = new Float32Array(cellsBuffer);
    const rMin = shape.profile[0].r;
    const rMax = shape.profile[shape.profile.length - 1].r;

    // Pick z extents from all cells (within R range) and the fit curve.
    let zMin = Infinity, zMax = -Infinity;
    for (let i = 0; i < cells.length; i += 4) {
      const r = cells[i + 1];
      if (r < rMin || r > rMax) continue;
      const z = cells[i + 2];
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
    const FIT_PTS = shape.segments ? 1000 : 200;
    for (let i = 0; i < FIT_PTS; i++) {
      const r = rMin + (rMax - rMin) * (i / (FIT_PTS - 1));
      const z = shape.predict(r);
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
    if (!Number.isFinite(zMin) || !Number.isFinite(zMax) || zMin === zMax) {
      zMin = -1; zMax = 1;
    }
    const zPad = (zMax - zMin) * 0.06 || 1;
    zMin -= zPad; zMax += zPad;

    const xScale = (x) => margins.left + ((x - rMin) / (rMax - rMin)) * innerW;
    const yScale = (z) => margins.top + innerH - ((z - zMin) / (zMax - zMin)) * innerH;

    // Axes.
    ctx.strokeStyle = axisColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margins.left, margins.top);
    ctx.lineTo(margins.left, margins.top + innerH);
    ctx.lineTo(margins.left + innerW, margins.top + innerH);
    ctx.stroke();

    // Tick marks + labels. Sizes proportional to the SCALEd canvas
    // so the chart matches the scanline-pair chart's visual weight
    // (which uses the shared .defo-tick-label / .defo-axis-label
    // classes at 10/11 px in user units; here we draw onto canvas
    // and multiply by SCALE).
    ctx.lineWidth = SCALE * 0.7;
    ctx.fillStyle = labelColor;
    ctx.font = `${10 * SCALE}px Arial, sans-serif`;   // match the scanline plot's tick labels
    const xTicks = niceTicks(rMin, rMax, 6);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const t of xTicks) {
      const x = xScale(t);
      ctx.beginPath();
      ctx.moveTo(x, margins.top + innerH);
      ctx.lineTo(x, margins.top + innerH + 4 * SCALE);
      ctx.stroke();
      ctx.fillText(String(Math.round(t)), x, margins.top + innerH + 6 * SCALE);
    }
    const zTicks = niceTicks(zMin, zMax, 6);
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (const t of zTicks) {
      const y = yScale(t);
      ctx.beginPath();
      ctx.moveTo(margins.left - 4 * SCALE, y);
      ctx.lineTo(margins.left, y);
      ctx.stroke();
      ctx.fillText(String(Math.round(t)), margins.left - 6 * SCALE, y);
    }

    // Axis labels.
    ctx.fillStyle = titleColor;
    ctx.font = `bold ${11 * SCALE}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('R (mm)', margins.left + innerW / 2, H - 6 * SCALE);
    ctx.save();
    ctx.translate(14 * SCALE, margins.top + innerH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Z (mm)', 0, 0);
    ctx.restore();

    // Piecewise segment boundaries (spheroid mode only).
    if (shape.segments && shape.segments.length > 1) {
      ctx.strokeStyle = segColor;
      ctx.setLineDash([2, 4]);
      ctx.globalAlpha = 0.6;
      for (let i = 1; i < shape.segments.length; i++) {
        const x = xScale(shape.segments[i].rMin);
        ctx.beginPath();
        ctx.moveTo(x, margins.top);
        ctx.lineTo(x, margins.top + innerH);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // Blue dots — every cell. fillRect is faster than arc() at this
    // density.
    ctx.fillStyle = dotColor;
    const xL = margins.left, xR = margins.left + innerW;
    const yT = margins.top,  yB = margins.top + innerH;
    const dot = Math.max(1, Math.round(SCALE * 0.7));
    for (let i = 0; i < cells.length; i += 4) {
      const r = cells[i + 1];
      const z = cells[i + 2];
      const px = xScale(r);
      const py = yScale(z);
      if (px < xL || px > xR || py < yT || py > yB) continue;
      ctx.fillRect(px - dot / 2, py - dot / 2, dot, dot);
    }

    // Red dashed fit line.
    ctx.strokeStyle = fitColor;
    ctx.lineWidth = SCALE * 0.6;
    ctx.setLineDash([4 * SCALE, 3 * SCALE]);
    ctx.beginPath();
    for (let i = 0; i < FIT_PTS; i++) {
      const r = rMin + (rMax - rMin) * (i / (FIT_PTS - 1));
      const z = shape.predict(r);
      const px = xScale(r);
      const py = yScale(z);
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }, [shape, cellsBuffer, isDark, W, H, innerW, innerH, margins.left, margins.right, margins.top, margins.bottom]);

  if (!shape || !shape.profile || shape.profile.length === 0) {
    return <div className="defo-chart-empty">No floor profile available.</div>;
  }
  return (
    <canvas
      ref={canvasRef}
      width={W}
      height={H}
      style={{ display: 'block', width: '100%', height: 'auto' }}
    />
  );
}

// ── Floor C2M canvas (top-down, coloured by deviation from the
//     fitted floor profile + integrated legend on the right) ────────
// Layout mirrors the shell unrolled view:
//   • 3306 × 1800 px canvas, black background everywhere
//   • Right ~263 px reserved for the legend (numeric scale + vertical
//     gradient bar + filled line-graph histogram). The legend block is
//     offset 50 px from the right edge.
//   • Left ~2993 px is the tank section; the floor itself is a circle
//     centred in that sub-frame with diameter = 75 % of the shorter
//     dimension (always 1350 px on a 3306×1800 canvas).
//   • Each (R, θ) cell is plotted at (R sin θ, R cos θ) and coloured
//     by c2m = z_actual − z_theoretical. z_theoretical comes from the
//     shape's `predict(R)` — which is piecewise across 1 m segments
//     for spheroid mode, single-formula for flat/cone.
function FloorC2MCanvas({
  cellsBuffer, shape, nominalRMm, thetaBinDeg = 1,
  cMin, cMax, histogram, angles,
  canvasRef: externalCanvasRef,
}) {
  const internalCanvasRef = useRef(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const CANVAS_W = C2M_CANVAS.WIDTH;
  const CANVAS_H = C2M_CANVAS.HEIGHT;
  const legendAreaX = C2M_LEGEND.AREA_X;

  // Tank section: everything to the left of the legend block. Floor is
  // circular, so it picks whichever of (tank-section-width, canvas-
  // height) is smaller as the constraining axis. Diameter = 75 % of
  // that constrained dimension. Centred inside the tank section.
  const tankSectionW = legendAreaX;
  const floorD = Math.round(0.75 * Math.min(tankSectionW, CANVAS_H));
  const floorR = floorD / 2;
  const floorCx = Math.round(tankSectionW / 2);
  const floorCy = Math.round(CANVAS_H / 2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    if (!cellsBuffer || !shape || !Number.isFinite(cMin) || !Number.isFinite(cMax)) return;
    const cells = new Float32Array(cellsBuffer);
    const pxPerMm = floorR / Math.max(1, nominalRMm);

    // Compute the cells' axis-aligned XY bounding box in mm. The box
    // centre — not the canvas centre — is the right origin for the
    // scanline arrows: the cloud isn't always perfectly symmetric
    // around (0, 0) after untilt, and pinning arrows to the canvas
    // centre then makes the rim gap visibly uneven across θ. Using
    // the box centre keeps the arrows centred on the actual disc.
    let xMinMm = Infinity, xMaxMm = -Infinity;
    let yMinMm = Infinity, yMaxMm = -Infinity;
    for (let i = 0; i < cells.length; i += 4) {
      const theta = cells[i];
      const r     = cells[i + 1];
      const tr = (theta * Math.PI) / 180;
      const xMm = r * Math.sin(tr);
      const yMm = r * Math.cos(tr);
      if (xMm < xMinMm) xMinMm = xMm;
      if (xMm > xMaxMm) xMaxMm = xMm;
      if (yMm < yMinMm) yMinMm = yMm;
      if (yMm > yMaxMm) yMaxMm = yMm;
    }
    const haveBox = Number.isFinite(xMinMm) && Number.isFinite(yMinMm);
    const boxCxMm = haveBox ? (xMinMm + xMaxMm) / 2 : 0;
    const boxCyMm = haveBox ? (yMinMm + yMaxMm) / 2 : 0;
    const boxHalfWMm = haveBox ? (xMaxMm - xMinMm) / 2 : nominalRMm;
    const boxHalfHMm = haveBox ? (yMaxMm - yMinMm) / 2 : nominalRMm;

    // ── Pixel-aggregation paint ─────────────────────────────────
    // For each canvas pixel within the disc's bounding box we want
    // the average c2m of every cell that contributes to it. The
    // per-cell footprint is its 1° × 1 mm wedge — at the rim that
    // wedge spans ~12 px tangentially × < 1 px radially, so we walk
    // each cell along its tangential direction and accumulate one
    // pixel per integer step. The result is gap-free regardless of
    // tank diameter, because every pixel inside the box gets at
    // least one cell's worth of contribution (or zero, in which case
    // it stays as the canvas's black background).
    if (haveBox) {
      const thetaBinRad = (thetaBinDeg * Math.PI) / 180;
      const PAD = 8;
      const xMinPxRaw = Math.floor(floorCx + xMinMm * pxPerMm) - PAD;
      const xMaxPxRaw = Math.ceil (floorCx + xMaxMm * pxPerMm) + PAD;
      const yMinPxRaw = Math.floor(floorCy - yMaxMm * pxPerMm) - PAD;
      const yMaxPxRaw = Math.ceil (floorCy - yMinMm * pxPerMm) + PAD;
      const bx = Math.max(0, xMinPxRaw);
      const by = Math.max(0, yMinPxRaw);
      const bw = Math.min(CANVAS_W, xMaxPxRaw) - bx;
      const bh = Math.min(CANVAS_H, yMaxPxRaw) - by;
      if (bw > 0 && bh > 0) {
        const total = bw * bh;
        const sumC2M = new Float32Array(total);
        const count  = new Uint32Array(total);
        const range  = cMax - cMin;

        for (let i = 0; i < cells.length; i += 4) {
          const theta = cells[i];
          const r     = cells[i + 1];
          const z     = cells[i + 2];
          const tr    = (theta * Math.PI) / 180;
          const sinT  = Math.sin(tr);
          const cosT  = Math.cos(tr);
          const cxPx  = floorCx + r * sinT * pxPerMm;
          const cyPx  = floorCy - r * cosT * pxPerMm;
          const c2m   = z - shape.predict(r);
          // Tangential direction in canvas: (cos θ, sin θ) — see
          // c2mRender.js conventions for the floor's coord system.
          const tangX = cosT;
          const tangY = sinT;
          // How many 1-px steps to lay down along the tangent. At
          // small R this is 1 (cells already overlap heavily); at
          // the rim of a 15 m tank it's ~12, which closes the gaps
          // between adjacent θ-bins.
          const tangPx = r * thetaBinRad * pxPerMm;
          const steps = Math.max(1, Math.round(tangPx));
          const half  = (steps - 1) / 2;
          for (let s = 0; s < steps; s++) {
            const off = s - half;
            const px = Math.round(cxPx + tangX * off);
            const py = Math.round(cyPx + tangY * off);
            const ix = px - bx;
            const iy = py - by;
            if (ix < 0 || ix >= bw || iy < 0 || iy >= bh) continue;
            const idx = iy * bw + ix;
            sumC2M[idx] += c2m;
            count[idx]++;
          }
        }

        // Paint via ImageData (one putImageData call) — about 5×
        // faster than poking the canvas with fillRect per pixel and
        // avoids the canvas state-machine overhead.
        if (range > 0) {
          const image = ctx.createImageData(bw, bh);
          const data  = image.data;
          for (let i = 0; i < total; i++) {
            const j = i * 4;
            if (count[i] > 0) {
              const avg = sumC2M[i] / count[i];
              const pct = ((avg - cMin) / range) * 100;
              const [rr, gg, bb] = rgbAtPercent(pct);
              data[j]     = rr;
              data[j + 1] = gg;
              data[j + 2] = bb;
            }
            // Empty pixels: keep RGB at 0 (black) so they read as
            // canvas background, but force alpha to 255 so the
            // putImageData REPLACE semantics don't punch transparent
            // holes in our black background.
            data[j + 3] = 255;
          }
          ctx.putImageData(image, bx, by);
        }
      }
    }

    // ── Scanline markers ────────────────────────────────────────
    // Each pair (SL_i_, SL_i+N/2_) is drawn as ONE diametric
    // double-headed arrow that passes through the FLOOR'S BOUNDING
    // BOX CENTRE — not the canvas centre. The box centre is the
    // midpoint of the cells' axis-aligned (x, y) extents, so the
    // arrow ring stays balanced even when the cloud is offset from
    // the canvas origin (which it commonly is, even after untilt).
    //
    // tipDist is the box's corner distance + 50 px: that's the
    // smallest radius that guarantees every arrow tip clears every
    // corner of the box by ≥ 50 px in any θ direction. Labels sit
    // a further 50 px past each tip along the same radial.
    if (Array.isArray(angles) && angles.length > 0 && haveBox) {
      const ext = C2M_ARROW.EXTENSION;
      const arrowCx = floorCx + boxCxMm * pxPerMm;
      const arrowCy = floorCy - boxCyMm * pxPerMm;
      // Tip distance = nominal R (in px) + 50 px overshoot.
      // Since pxPerMm = floorR / nominalRMm, this works out to
      // floorR + EXTENSION, but expressed in terms of nominal R so
      // the intent is clear at the call site.
      const tipDist = nominalRMm * pxPerMm + ext;
      const LABEL_GAP = 50;
      const labelDist = tipDist + LABEL_GAP;
      const halfN = Math.floor(angles.length / 2);

      ctx.font = C2M_ARROW.LABEL_FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (let i = 0; i < halfN; i++) {
        const angleA = angles[i];
        const angleB = angles[i + halfN];
        if (!Number.isFinite(angleA)) continue;
        const thetaA = (angleA * Math.PI) / 180;
        const sinA = Math.sin(thetaA);
        const cosA = Math.cos(thetaA);

        const tipAX = arrowCx + sinA * tipDist;
        const tipAY = arrowCy - cosA * tipDist;
        const tipBX = arrowCx - sinA * tipDist;
        const tipBY = arrowCy + cosA * tipDist;
        drawScanlineArrow(ctx, {
          x1: tipBX, y1: tipBY,
          x2: tipAX, y2: tipAY,
          headEnds: 'both',
        });

        ctx.fillStyle = '#FFFFFF';
        const labelAX = arrowCx + sinA * labelDist;
        const labelAY = arrowCy - cosA * labelDist;
        ctx.fillText(`SL${i + 1} (${angleA}°)`, labelAX, labelAY);
        if (Number.isFinite(angleB)) {
          const labelBX = arrowCx - sinA * labelDist;
          const labelBY = arrowCy + cosA * labelDist;
          ctx.fillText(`SL${i + 1 + halfN} (${angleB}°)`, labelBX, labelBY);
        }
      }
    }

    // ── Legend block on the right edge (shared helper) ──────────
    drawC2MLegend(ctx, { cMin, cMax, histogram });
  }, [cellsBuffer, shape, nominalRMm, thetaBinDeg, cMin, cMax, histogram, angles,
      floorR, floorCx, floorCy, legendAreaX, CANVAS_W, CANVAS_H]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      className="defo-unrolled-canvas"
    />
  );
}

// ── XY scatter (top-down) test output ───────────────────────────────
// One white dot per occupied (R, θ) cell. θ = 0 is +Y, increasing
// clockwise — same convention as the shell tool. Tank centre at canvas
// centre; scale fits maxR + a small margin into the smaller canvas
// dimension so the floor looks circular (1:1 aspect).
function FloorXYScatter({ cellsBuffer, cellCount, tilt, canvasRef: externalCanvasRef }) {
  // The worker reports `tilt` with the pre-untilt normal. Derive a
  // unit XY-rise direction for the red arrows.
  const normal = tilt ? (() => {
    const mag = Math.sqrt(tilt.nx * tilt.nx + tilt.ny * tilt.ny);
    const rise = mag > 1e-9 ? { x: tilt.nx / mag, y: tilt.ny / mag } : { x: 0, y: 0 };
    return {
      nx: tilt.nx, ny: tilt.ny, nz: tilt.nz,
      tiltDeg: tilt.tiltDeg,
      rise,
    };
  })() : null;
  const internalCanvasRef = useRef(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const SIZE = 1200;
  const MARGIN = 24;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, SIZE, SIZE);

    if (!cellsBuffer || cellCount === 0) return;
    const cells = new Float32Array(cellsBuffer);

    let maxR = 0;
    for (let i = 1; i < cells.length; i += 4) {
      const r = cells[i];
      if (r > maxR) maxR = r;
    }
    if (maxR === 0) maxR = 1;

    const innerW = SIZE - 2 * MARGIN;
    const scale = innerW / (2 * maxR);
    const cx = SIZE / 2;
    const cy = SIZE / 2;

    // Crosshair through the origin (no outer envelope circle).
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(MARGIN, cy);
    ctx.lineTo(SIZE - MARGIN, cy);
    ctx.moveTo(cx, MARGIN);
    ctx.lineTo(cx, SIZE - MARGIN);
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    for (let i = 0; i < cells.length; i += 4) {
      const thetaDeg = cells[i];
      const rMm = cells[i + 1];
      const thetaRad = (thetaDeg * Math.PI) / 180;
      const xMm = rMm * Math.sin(thetaRad);
      const yMm = rMm * Math.cos(thetaRad);
      const px = cx + xMm * scale;
      const py = cy - yMm * scale;
      ctx.fillRect(px, py, 1, 1);
    }

    // ── Floor-normal indicator ──────────────────────────────────
    // Two red arrows along the floor's tilt axis, drawn from the
    // origin. One in the (+nx, +ny) direction (the "uphill" side),
    // the other in the opposite (-nx, -ny) direction (the "downhill"
    // side). Arrow length is fixed so the direction is visible even
    // for nearly-flat floors. The Z component is annotated in text.
    // Only draw the tilt arrows when the tilt is meaningfully non-zero.
    // The previous |nx|+|ny| > 1e-9 threshold was so small it caught any
    // floating-point dust and drew arrows even when the displayed tilt
    // read "0.000°" — so gate on the actual tilt magnitude in degrees.
    if (normal && normal.tiltDeg > 0.01) {
      const ARROW_PX  = SIZE * 0.32;
      const HEAD_LEN  = 36;
      const HEAD_WID  = 22;
      const SHAFT     = 6;
      ctx.strokeStyle = '#FF0000';
      ctx.fillStyle   = '#FF0000';
      ctx.lineWidth   = SHAFT;
      const drawArrow = (dirX, dirY) => {
        const tipX = cx + dirX * ARROW_PX;
        // Canvas Y is inverted relative to world Y, so subtract.
        const tipY = cy - dirY * ARROW_PX;
        const dx = tipX - cx;
        const dy = tipY - cy;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) return;
        const ux = dx / len, uy = dy / len;
        const baseX = tipX - ux * HEAD_LEN;
        const baseY = tipY - uy * HEAD_LEN;
        const perpX = -uy * HEAD_WID;
        const perpY =  ux * HEAD_WID;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(baseX, baseY);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(baseX + perpX, baseY + perpY);
        ctx.lineTo(baseX - perpX, baseY - perpY);
        ctx.closePath();
        ctx.fill();
      };
      drawArrow(normal.rise.x,  normal.rise.y);   // uphill
      drawArrow(-normal.rise.x, -normal.rise.y);  // downhill
    }

    // Normal-vector legend (bottom-left of canvas).
    if (normal) {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '20px Calibri, "Calibri Body", Arial, Helvetica, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      const lines = [
        `normal = (${normal.nx.toFixed(4)}, ${normal.ny.toFixed(4)}, ${normal.nz.toFixed(4)})`,
        `tilt   = ${normal.tiltDeg.toFixed(3)}°  (rise direction = +arrow, fall direction = −arrow)`,
      ];
      const lineH = 24;
      const baseY = SIZE - MARGIN;
      for (let i = lines.length - 1; i >= 0; i--) {
        ctx.fillText(lines[i], MARGIN + 4, baseY - (lines.length - 1 - i) * lineH);
      }
    }
  }, [cellsBuffer, cellCount, tilt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      width={SIZE}
      height={SIZE}
      className="defo-unrolled-canvas"
    />
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export default function FloorDeformationPage() {
  const { id: clientId, objectId, inspectionId } = useParams();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();
  const { setSaveHandler } = useAnalysisActions();
  // Save-and-close capture refs.
  const scanlineGridRef    = useRef(null);
  const xyScatterCanvasRef = useRef(null);
  const c2mCanvasRef       = useRef(null);
  // Mount marker — see comment in ShellDeformationPage.
  // eslint-disable-next-line no-console
  useEffect(() => { console.log('[FloorDeformationPage] mounted'); }, []);

  // Parent client / object / inspection — fetched so we can show
  // human-readable names in the breadcrumb. Falls back to IDs if any
  // of the calls fail or while they're still in flight.
  const [breadcrumbCtx, setBreadcrumbCtx] = useState({ client: null, object: null, inspection: null });
  // Only render the breadcrumb once the lookups resolve — otherwise the first
  // paint shows raw UUID fallbacks that blink to the real names a moment later.
  const [breadcrumbResolved, setBreadcrumbResolved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, o, i] = await Promise.all([
          getClientById(clientId),
          getObjectById(objectId),
          getInspectionById(inspectionId),
        ]);
        if (!cancelled) setBreadcrumbCtx({ client: c, object: o, inspection: i });
      } catch {
        if (!cancelled) setBreadcrumbCtx({ client: null, object: null, inspection: null });
      } finally {
        if (!cancelled) setBreadcrumbResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, objectId, inspectionId]);

  useEffect(() => {
    if (!breadcrumbResolved) return undefined;
    const { client, object, inspection } = breadcrumbCtx;
    const crumbs = [];
    crumbs.push({ label: client?.name      || `Client ${clientId}`,         path: null });
    crumbs.push({ label: object?.objectTag || `Object ${objectId}`,         path: null });
    crumbs.push({ label: inspection?.type  || `Inspection ${inspectionId}`, path: null });
    crumbs.push({ label: 'EEMUA 159 Floor Deformation Analysis', path: null, bold: true });
    setBreadcrumb(crumbs);
    return () => clearBreadcrumb();
  }, [breadcrumbResolved, breadcrumbCtx, clientId, objectId, inspectionId, setBreadcrumb, clearBreadcrumb]);

  // EEMUA 159 is the finalized standard for this analysis. No standard
  // selector / sidebar — the page is dedicated to EEMUA 159.
  const [standard] = useState('eemua159');

  // Configuration — the Tank-geometry panel is gone; these defaults
  // are baked in. Scanline count is still state because it's auto-
  // derived from the worker result. Angles remain editable per
  // scanline within their ±360/(2N) bounds (paired across).
  const thetaBin   = 1;     // θ-bin size in degrees
  const rImageStep = 100;   // mm — R-slabbing for the scanline-pair charts
  const [scanlineCount, setScanlineCount] = useState(8);
  const [angles,        setAngles]        = useState(() => defaultAnglesFor(8));
  const [file,          setFile]          = useState(null);

  useEffect(() => { setAngles(defaultAnglesFor(scanlineCount)); }, [scanlineCount]);

  const [status,       setStatus]       = useState('idle');
  const [progressMsgs, setProgressMsgs] = useState([]);
  const [progress,     setProgress]     = useState({ stage: '', pct: 0 });
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState('');
  const workerRef = useRef(null);

  // Brief "recomputing" flash for the progress modal (used when a
  // scanline angle is committed). See ShellDeformationPage for the
  // same pattern.
  const [recompute, setRecompute] = useState(false);
  const recomputeTimerRef = useRef(null);
  const flashRecompute = () => {
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    setRecompute(true);
    recomputeTimerRef.current = setTimeout(() => setRecompute(false), 350);
  };

  // Auto-derive the scanline count whenever the worker reports a
  // nominal D. The effect on `scanlineCount` above then resets angles
  // to equally-spaced defaults for the new count.
  useEffect(() => {
    if (result?.nominal?.D_mm) {
      setScanlineCount(autoScanlineCount(result.nominal.D_mm));
    }
  }, [result]);

  function appendMsg(msg) {
    setProgressMsgs((prev) => [...prev, { t: Date.now(), msg }]);
  }

  // Clear every piece of run-state that's tied to the previously-loaded
  // file. Called when the user picks a new file (even one with the same
  // name) so we never end up with a half-detached ArrayBuffer or a
  // still-running worker holding the old data.
  function resetForNewFile() {
    if (workerRef.current) {
      try { workerRef.current.terminate(); } catch (e) { /* noop */ }
      workerRef.current = null;
    }
    setResult(null);
    setError('');
    setProgressMsgs([]);
    setProgress({ stage: '', pct: 0 });
    setStatus('idle');
    if (typeof window !== 'undefined') window.__floorResult = null;
  }

  // Paired-angle setter (floor only): if the user edits scanline i in
  // the first half, the opposite scanline (i + N/2) auto-locks to the
  // +180° complement. Edits to the second half are ignored — those
  // inputs are read-only in the UI.
  function setAngle(idx, val) {
    const half = scanlineCount / 2;
    setAngles((arr) => {
      const next = [...arr];
      if (idx >= half) return next; // second-half is derived only
      next[idx] = val;
      next[idx + half] = (((val + 180) % 360) + 360) % 360;
      return next;
    });
  }

  // ── Download coloured 3D point cloud (.txt ASCII) ──────────────────
  // Re-parses the original file and emits every point in the UN-TILTED
  // frame, coloured by its floor C2M cell (avg untilted Z − fitted shape).
  const [exporting, setExporting] = useState(false);
  async function handleDownloadModel() {
    if (exporting || !result || !c2mStats || !shape || !file || !result.tilt) return;
    setExporting(true);
    try {
      const cells = new Float32Array(result.cellsBuffer);
      const binsPerCircle = result.binsPerCircle;
      const { cMin, cMax } = c2mStats;
      const range = cMax - cMin;
      const colorMap = new Map();
      for (let i = 0; i < cells.length; i += 4) {
        const theta = cells[i];
        const rMm   = cells[i + 1];
        const z     = cells[i + 2];
        let binIdx = Math.round(theta / thetaBin) % binsPerCircle;
        if (binIdx < 0) binIdx += binsPerCircle;
        const key = rMm * binsPerCircle + binIdx;
        const c2m = z - shape.predict(rMm);
        const pct = range > 0 ? ((c2m - cMin) / range) * 100 : 50;
        const [rr, gg, bb] = rgbAtPercent(pct);
        colorMap.set(key, (rr << 16) | (gg << 8) | bb);
      }

      const buffer = await file.arrayBuffer();
      const worker = new Worker(new URL('../workers/pointCloudExport.worker.js', import.meta.url));
      worker.onmessage = (ev) => {
        const m = ev.data;
        if (m.kind === 'done') {
          const tag = breadcrumbCtx?.object?.objectTag || 'model';
          const blob = new Blob([m.text], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${tag}_Floor_C2M.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          worker.terminate();
          setExporting(false);
        } else if (m.kind === 'error') {
          // eslint-disable-next-line no-alert
          alert('Export failed: ' + m.message);
          worker.terminate();
          setExporting(false);
        }
      };
      worker.onerror = (e) => {
        // eslint-disable-next-line no-alert
        alert('Export failed: ' + (e?.message || 'worker error'));
        worker.terminate();
        setExporting(false);
      };
      worker.postMessage({
        mode: 'floor',
        buffer,
        colorMap,
        cx: result.center.cx,
        cy: result.center.cy,
        zRef: result.center.zRef,
        thetaBinDeg: thetaBin,
        binsPerCircle,
        centroid: result.tilt.centroid,
        rotXrad: result.tilt.rotXrad,
        rotYrad: result.tilt.rotYrad,
      }, [buffer]);
    } catch (e) {
      setExporting(false);
      // eslint-disable-next-line no-alert
      alert('Export failed: ' + (e?.message || e));
    }
  }

  const allAnglesValid = useMemo(
    () => angles.every((a, i) => isValidAngle(a, i, scanlineCount)),
    [angles, scanlineCount],
  );

  // Index the (already-untilted) floor cloud by θ-bin and aggregate R
  // into uniform slabs of `rImageStep` mm. With the default 1 mm the
  // slabbing is a no-op; coarser values smooth the scanline profiles.
  const slabbedByBin = useMemo(() => {
    const map = new Map();
    if (!result || !result.cellsBuffer) return map;
    const cells = new Float32Array(result.cellsBuffer);
    const { binsPerCircle, thetaBinDeg } = result;
    const slab = Math.max(1, rImageStep);
    const acc = new Map(); // bin → Map(slabR → { sumZ, count })
    for (let i = 0; i < cells.length; i += 4) {
      const theta = cells[i];
      const r     = cells[i + 1];
      const z     = cells[i + 2];
      const bin = (((Math.round(theta / thetaBinDeg) % binsPerCircle) + binsPerCircle) % binsPerCircle);
      let slabMap = acc.get(bin);
      if (!slabMap) { slabMap = new Map(); acc.set(bin, slabMap); }
      const slabR = Math.round(r / slab) * slab;
      const e = slabMap.get(slabR);
      if (e) { e.sumZ += z; e.count++; }
      else   { slabMap.set(slabR, { sumZ: z, count: 1 }); }
    }
    for (const [bin, slabMap] of acc) {
      const arr = [];
      for (const [r, e] of slabMap) arr.push({ r, z: e.sumZ / e.count });
      arr.sort((a, b) => a.r - b.r);
      map.set(bin, arr);
    }
    return map;
  }, [result, rImageStep]);

  // Tilt info (normal, rot-X, rot-Y, tilt deg) is reported by the
  // worker — computed on the raw cloud before the untilt rotation, so
  // it actually reflects how the floor was tilted. The binned cells
  // themselves are already untilted, so deriving a normal from them
  // here would just give ≈ (0, 0, 1).
  const tilt = result?.tilt || null;

  // User override for the shape classifier's initial guess. null = use
  // the auto-detected type. When set, classifyFloorShape skips the
  // detection thresholds and uses this directly; the cross-section
  // chart, c2mStats, histogram, and the C2M canvas all re-derive
  // through the memo cascade, so no re-parse / re-bin is needed.
  const [userClassification, setUserClassification] = useState(null);
  // Picker UI state. `pickerOpen` controls whether the dropdown is
  // visible at all; `pendingChoice` is the dropdown's current value
  // (not yet applied until the Regenerate button is clicked).
  const [pickerOpen,      setPickerOpen]      = useState(false);
  const [pendingChoice,   setPendingChoice]   = useState(null);

  // `regenerating` shows the full-screen progress modal during a
  // Regenerate click. The shape useMemo recomputes synchronously, so
  // without a deliberate delay the modal would never render; we open
  // it, defer the override-apply by a tick, then close after ~500ms
  // so the user sees an unambiguous "something just happened".
  const [regenerating, setRegenerating] = useState(false);
  const regenTimerRef = useRef(null);
  const handleRegenerate = (choice) => {
    setRegenerating(true);
    setTimeout(() => {
      setUserClassification(choice);
      if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
      regenTimerRef.current = setTimeout(() => setRegenerating(false), 500);
    }, 0);
  };

  // Floor-shape classification — fits a linear + quadratic to the
  // averaged Z(R) profile of the (already-untilted) cells. Override is
  // applied here so all downstream views see the chosen model.
  const shape = useMemo(() => {
    if (!result?.cellsBuffer || !result?.nominal?.R_mm) return null;
    return classifyFloorShape(result.cellsBuffer, result.nominal.R_mm, userClassification);
  }, [result, userClassification]);

  // Reset the override + close the picker whenever the source result
  // changes (i.e. the user built a new cloud).
  useEffect(() => {
    setUserClassification(null);
    setPickerOpen(false);
    setPendingChoice(null);
  }, [result]);

  // C2M = z_actual − z_theoretical, computed per cell. cMin/cMax drive
  // the colour scale; the (smoothed) histogram is built over the same
  // domain so the legend bars and the floor image agree pixel-for-pixel.
  const c2mStats = useMemo(() => {
    if (!result?.cellsBuffer || !shape) return null;
    const cells = new Float32Array(result.cellsBuffer);
    let cMin = Infinity, cMax = -Infinity, n = 0;
    for (let i = 0; i < cells.length; i += 4) {
      const r = cells[i + 1];
      const z = cells[i + 2];
      const c2m = z - shape.predict(r);
      if (c2m < cMin) cMin = c2m;
      if (c2m > cMax) cMax = c2m;
      n++;
    }
    if (!Number.isFinite(cMin)) return null;
    return { cMin, cMax, range: cMax - cMin, totalCells: n };
  }, [result, shape]);

  const histogramData = useMemo(() => {
    if (!result?.cellsBuffer || !shape || !c2mStats || c2mStats.range <= 0) return null;
    const cells = new Float32Array(result.cellsBuffer);
    const NBINS = 200;
    const SMOOTH_RADIUS = 3;
    const { cMin, cMax, range } = c2mStats;
    const binWidth = range / NBINS;
    const rawCounts = new Int32Array(NBINS);
    for (let i = 0; i < cells.length; i += 4) {
      const r = cells[i + 1];
      const z = cells[i + 2];
      const c2m = z - shape.predict(r);
      let idx = Math.floor((c2m - cMin) / binWidth);
      if (idx >= NBINS) idx = NBINS - 1;
      if (idx < 0) idx = 0;
      rawCounts[idx]++;
    }
    // Box-filter smoothing (same as shell) — bridges zero gaps from
    // narrow bin widths and gives the line graph a continuous shape.
    const counts = new Float32Array(NBINS);
    for (let i = 0; i < NBINS; i++) {
      let sum = 0, k = 0;
      const lo = Math.max(0, i - SMOOTH_RADIUS);
      const hi = Math.min(NBINS - 1, i + SMOOTH_RADIUS);
      for (let j = lo; j <= hi; j++) { sum += rawCounts[j]; k++; }
      counts[i] = sum / k;
    }
    let maxCount = 0;
    for (let i = 0; i < NBINS; i++) if (counts[i] > maxCount) maxCount = counts[i];
    return { counts, binWidth, cMin, cMax, maxCount, NBINS, totalCells: c2mStats.totalCells };
  }, [result, shape, c2mStats]);

  // ── Settlement analysis ────────────────────────────────────────
  // Three computations sharing the same data source (`slabbedByBin`,
  // the R-slabbed cloud that also feeds the scanline pair charts):
  //   1) Local differential settlement — |Z_rim − Z_rim_neighbour|
  //      for each scanline against its left/right neighbours
  //      (circular wrap). Allowable = 1 % × arc length =
  //      1 % × (π · D) / N.
  //   2) General differential settlement — max(Z_rim) − min(Z_rim)
  //      across all scanlines. Allowable S_max depends on D.
  //   3) Edge settlement — within the 750 mm band inboard from
  //      each scanline's rim, find Z_max and the R where it occurs.
  //      The breakpoint distance is 750 mm when Z_max sits at the
  //      rim or at the inner edge of the band; otherwise it's
  //      (R_rim − R_at_Zmax). Allowable = breakpoint / 6.
  const settlementData = useMemo(() => {
    if (!result?.cellsBuffer || !slabbedByBin || slabbedByBin.size === 0) return null;
    if (!Array.isArray(angles) || angles.length === 0) return null;
    if (!result?.nominal?.D_mm) return null;

    const { thetaBinDeg, binsPerCircle } = result;
    const nominalDmm = result.nominal.D_mm;
    const n = angles.length;

    // Per-scanline rim + 750 mm band.
    const slData = angles.map((angle, idx) => {
      if (!Number.isFinite(angle)) {
        return { scanlineN: idx + 1, angle, rRim: null, zRim: null, band: [] };
      }
      const binIdx = (((Math.round(angle / thetaBinDeg) % binsPerCircle) + binsPerCircle) % binsPerCircle);
      const pts = slabbedByBin.get(binIdx) || [];
      if (pts.length === 0) {
        return { scanlineN: idx + 1, angle, rRim: null, zRim: null, band: [] };
      }
      const rimPt = pts[pts.length - 1];
      const band = pts.filter((p) => p.r >= rimPt.r - 750);
      return {
        scanlineN: idx + 1,
        angle,
        rRim: rimPt.r,
        zRim: rimPt.z,
        band,
      };
    });

    // ── 1. Local differential settlement ─────────────────────
    const arcLengthMm = (Math.PI * nominalDmm) / n;
    const localAllowMm = 0.01 * arcLengthMm;
    const localDiff = slData.map((sl, i) => {
      const leftIdx  = (i - 1 + n) % n;
      const rightIdx = (i + 1) % n;
      const leftZ  = slData[leftIdx].zRim;
      const rightZ = slData[rightIdx].zRim;
      const dLeft  = (sl.zRim != null && leftZ  != null) ? Math.abs(sl.zRim - leftZ)  : null;
      const dRight = (sl.zRim != null && rightZ != null) ? Math.abs(sl.zRim - rightZ) : null;
      const valid  = [dLeft, dRight].filter((d) => d != null);
      const dMax   = valid.length > 0 ? Math.max(...valid) : null;
      const pct    = (dMax != null && localAllowMm > 0) ? (dMax / localAllowMm) * 100 : null;
      return {
        scanlineN: sl.scanlineN,
        angle: sl.angle,
        zRim: sl.zRim,
        leftSL:  slData[leftIdx].scanlineN,
        rightSL: slData[rightIdx].scanlineN,
        dLeft, dRight, dMax,
        allowable: localAllowMm,
        pct,
        pass: dMax != null ? dMax <= localAllowMm : null,
      };
    });

    // ── 2. General differential settlement ───────────────────
    const valid = slData.filter((s) => s.zRim != null);
    let zMax = -Infinity, zMin = Infinity;
    for (const s of valid) {
      if (s.zRim > zMax) zMax = s.zRim;
      if (s.zRim < zMin) zMin = s.zRim;
    }
    const tol = 0.5;
    const maxScanlines = valid.filter((s) => Math.abs(s.zRim - zMax) < tol).map((s) => s.scanlineN);
    const minScanlines = valid.filter((s) => Math.abs(s.zRim - zMin) < tol).map((s) => s.scanlineN);
    const generalDiff  = valid.length > 0 ? (zMax - zMin) : null;

    const D_m = nominalDmm / 1000;
    let sMaxMm;
    if (D_m <= 6)       sMaxMm = 100;
    else if (D_m >= 36) sMaxMm = 300;
    else                sMaxMm = (20 * D_m + 180) / 3;

    const generalPct = (generalDiff != null && sMaxMm > 0) ? (generalDiff / sMaxMm) * 100 : null;
    const general = {
      zMax: valid.length > 0 ? zMax : null,
      zMin: valid.length > 0 ? zMin : null,
      maxScanlines,
      minScanlines,
      diff: generalDiff,
      sMax: sMaxMm,
      pct:  generalPct,
      pass: generalDiff != null ? generalDiff <= sMaxMm : null,
    };

    // ── 3. Edge settlement ───────────────────────────────────
    const edgeSettlement = slData.map((sl) => {
      if (!sl.band || sl.band.length === 0 || sl.rRim == null) {
        return {
          scanlineN: sl.scanlineN, angle: sl.angle,
          rRim: sl.rRim, zRim: sl.zRim,
          zMaxBand: null, rAtZmax: null,
          breakpoint: null, allowable: null, measured: null,
          pct: null, pass: null,
        };
      }
      let zMaxBand = -Infinity;
      let rAtZmax = null;
      for (const p of sl.band) {
        if (p.z > zMaxBand) { zMaxBand = p.z; rAtZmax = p.r; }
      }
      const innerSlabZ = sl.band[0].z;
      const rimSlabR   = sl.band[sl.band.length - 1].r;
      const atRim = rAtZmax === rimSlabR;
      let breakpoint, measured;
      if (atRim) {
        // Z_max coincides with the rim — R_rim − R @ Z_max would be 0,
        // so use the 750 mm band span instead and fall back to the
        // (Z @ rim − Z @ inner band edge) for measured.
        breakpoint = 750;
        measured = Math.abs(sl.zRim - innerSlabZ);
      } else {
        // Z_max is anywhere else in the band — breakpoint is always the
        // actual radial gap between the rim and Z_max, even if Z_max
        // lands on the inner edge.
        breakpoint = sl.rRim - rAtZmax;
        measured = Math.abs(zMaxBand - sl.zRim);
      }
      const allowable = breakpoint / 6;
      const pct = allowable > 0 ? (measured / allowable) * 100 : null;
      return {
        scanlineN: sl.scanlineN, angle: sl.angle,
        rRim: sl.rRim, zRim: sl.zRim,
        zMaxBand, rAtZmax,
        breakpoint, allowable, measured,
        pct,
        pass: measured != null && allowable != null ? measured <= allowable : null,
      };
    });

    return {
      perScanline: slData,
      localDiff,
      general,
      edgeSettlement,
      arcLengthMm,
      localAllowMm,
      nominalDmm,
    };
  }, [result, slabbedByBin, angles]);

  async function handleStart() {
    if (!file) { setError('Please select a point cloud TXT file first.'); return; }
    if (!allAnglesValid) { setError('One or more scanline angles are outside their valid range.'); return; }
    // Belt-and-braces: kill any worker that's still around from a
    // previous run before we spin a new one up. Avoids two workers
    // contending for the same transferred buffer.
    if (workerRef.current) {
      try { workerRef.current.terminate(); } catch (e) { /* noop */ }
      workerRef.current = null;
    }
    setError('');
    setProgressMsgs([]);
    setResult(null);
    setProgress({ stage: '', pct: 0 });
    setStatus('running');
    appendMsg(`Reading "${file.name}" (${(file.size / (1024 * 1024)).toFixed(2)} MB) into memory…`);

    let buffer;
    try {
      buffer = await file.arrayBuffer();
    } catch (e) {
      setStatus('error');
      setError('Failed to read file: ' + e.message);
      return;
    }
    appendMsg(`File loaded. Spawning Web Worker…`);

    const worker = new Worker(new URL('../workers/floorDeformation.worker.js', import.meta.url));
    workerRef.current = worker;
    worker.onmessage = (ev) => {
      const m = ev.data;
      if (m.kind === 'stage') {
        appendMsg(m.message || m.stage);
        setProgress({ stage: m.stage, pct: 0 });
      } else if (m.kind === 'progress') {
        if (m.stage === 'parse') {
          setProgress({ stage: 'parse', pct: 0, label: `parsed ${m.points.toLocaleString()} pts / ${m.lines.toLocaleString()} lines` });
        } else if (m.total > 0) {
          const pct = Math.min(100, Math.round((m.done / m.total) * 100));
          setProgress({ stage: m.stage, pct });
        }
      } else if (m.kind === 'done') {
        window.__floorResult = m;
        appendMsg(`Total worker time: ${(m.durationMs / 1000).toFixed(2)} s.`);
        appendMsg(`Done. Floor cloud on window.__floorResult — quads of [θ_deg, R_mm, Z_avg_mm, count].`);
        // Pre-compute scanlineCount + default angles BEFORE closing
        // the progress modal so the user doesn't briefly see the
        // stale 8-scanline UI flash to the real count. React batches
        // all four setState calls into one commit, so by the time
        // the modal hides (status flips off 'running') the angles
        // + count are already correct.
        const finalCount = autoScanlineCount(m.nominal?.D_mm);
        setResult(m);
        setScanlineCount(finalCount);
        setAngles(defaultAnglesFor(finalCount));
        setStatus('done');
        setProgress({ stage: 'done', pct: 100 });
      } else if (m.kind === 'error') {
        appendMsg('Error: ' + m.message);
        setError(m.message);
        setStatus('error');
      }
    };
    worker.onerror = (e) => {
      setError('Worker error: ' + (e.message || 'unknown'));
      setStatus('error');
    };
    worker.postMessage({ buffer, thetaBinDeg: thetaBin }, [buffer]);
  }

  function handleAbort() {
    if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
    setStatus('idle');
    appendMsg('Aborted by user.');
  }

  const halfBin = (scanlineCount > 0) ? (360 / (2 * scanlineCount)) : 0;

  // ── Save-and-close handler ──────────────────────────────────
  useEffect(() => {
    if (!result || standard !== 'eemua159' || !inspectionId) {
      setSaveHandler(null);
      return undefined;
    }
    setSaveHandler(async () => {
      const { payload, images } = await bundleFloorResults({
        result,
        angles,
        settlementData,
        shape,
        scanlineGridEl: scanlineGridRef.current,
        xyScatterCanvasEl: xyScatterCanvasRef.current,
        c2mCanvasEl: c2mCanvasRef.current,
      });
      await post(`/inspections/${inspectionId}/defo-results`, {
        kind: 'floor',
        standard: 'eemua159',
        payload,
        images,
      });
    });
    return () => setSaveHandler(null);
  }, [result, standard, angles, settlementData, shape, inspectionId, setSaveHandler]);

  return (
    <div className="defo-page">
      <header className="defo-header">
        <h1>Floor Deformation Analysis</h1>
      </header>

      {/* ── Control card — heading reflects active standard, contains
            disclaimer + file picker + Run button (or placeholder for
            API 653). One canonical input control for the page. ─── */}
      <section className="defo-card defo-control-card">
        <div className="defo-standard-card-header">
          <h2 className="insp-info-title">EEMUA 159</h2>
        </div>
        <p className="defo-sub defo-upload-disclaimer">
          All processing runs locally in this browser tab — no data is sent
          to the server. <strong>Nothing is saved until you click Save and close.</strong>{' '}
          All results live in this tab; closing the tab without saving loses them.
        </p>
        <div className="defo-upload-row">
          <label className="defo-config-label">Point cloud ASCII file</label>
          <div className="defo-file-picker">
            <input
              id="defo-floor-file-input"
              type="file"
              accept=".txt,.asc,.xyz,.csv"
              className="defo-file-input-hidden"
              onClick={(e) => { e.currentTarget.value = ''; }}
              onChange={(e) => {
                resetForNewFile();
                setFile(e.target.files?.[0] || null);
              }}
              disabled={status === 'running'}
            />
            <label
              htmlFor="defo-floor-file-input"
              className={`btn btn-secondary defo-file-btn${status === 'running' ? ' disabled' : ''}`}
            >
              Choose file
            </label>
            <div className={`defo-file-display${file ? ' has-file' : ''}`}>
              <span className="defo-file-name">
                {file
                  ? `${file.name} · ${(file.size / (1024 * 1024)).toFixed(2)} MB`
                  : 'No file chosen'}
              </span>
              {file && (
                <button
                  type="button"
                  className="defo-file-clear"
                  onClick={() => { resetForNewFile(); setFile(null); }}
                  disabled={status === 'running'}
                  aria-label="Remove file"
                  title="Remove file"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="defo-actions">
          <button
            className="btn btn-primary defo-run-btn"
            onClick={handleStart}
            disabled={!file || status === 'running' || !allAnglesValid}
          >
            {status === 'running' ? 'Running…' : 'Run Deformation'}
          </button>
          {status === 'running' && (
            <button className="btn btn-secondary defo-run-btn" onClick={handleAbort}>Abort</button>
          )}
        </div>
      </section>

      {/* ── Results card — only appears once a run completes; the
            progress modal handles the running state. Never shown for
            API 653. ────────────────────────────────────────────── */}
      {standard === 'eemua159' && result && (
      <section className="defo-card defo-results-card">
        <div className="defo-standard-card-header">
          <h2 className="insp-info-title">Results</h2>
        </div>

      {/* ── Scanline angles — appears after the cloud is built ── */}
      {result && (
        <section className="defo-subcard">
          <h2>Scanline angles</h2>
          <p className="defo-card-hint">
            θ = 0° points along the +Y axis and increases clockwise through +X, −Y, −X.
            Scanline 1 defaults to 0°, the rest are equally spaced clockwise around the
            tank. Scanlines are paired across the tank. Each angle may be overridden
            within ±{halfBin.toFixed(2)}° of its default (integer values only).
            Changing an angle re-renders the outputs immediately; no rebuild needed.
          </p>
          <div className="defo-angles-grid">
            {angles.map((a, i) => {
              const half = scanlineCount / 2;
              const isPrimary = i < half;
              const b = angleBoundsFor(i, scanlineCount);
              const valid = isValidAngle(a, i, scanlineCount);
              const rangeText = isPrimary
                ? (b.wraps
                    ? `valid: ${b.lower}…359, 0…${b.upper}`
                    : `valid: ${b.lower}…${b.upper}`)
                : `paired with SL${i - half + 1} (+180°)`;
              return (
                <label key={i} className={`defo-angle ${valid ? '' : 'invalid'} ${isPrimary ? '' : 'paired'}`}>
                  <span>Scanline {i + 1}{isPrimary ? '' : ' (paired)'}</span>
                  <div className="defo-angle-input-row">
                    {isPrimary ? (
                      <AngleInput
                        value={a}
                        disabled={status === 'running'}
                        onCommit={(v) => {
                          setAngle(i, v);
                          flashRecompute();
                        }}
                      />
                    ) : (
                      <input
                        type="number"
                        value={a}
                        min={0}
                        max={359}
                        step={1}
                        readOnly
                        tabIndex={-1}
                        disabled
                      />
                    )}
                    <span className="defo-angle-unit">°</span>
                  </div>
                  <small>{rangeText}</small>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {result && (
        <section className="defo-subcard defo-stats">
          <h2>Nominal geometry</h2>
          <p className="defo-card-hint">
            The floor's rim points define the tank outline: their mean radius is the
            nominal radius (R), and the diameter is D = 2R. Nominal height (H) is the
            Z-extent of the cloud. σ is the spread of the rim radii about that mean.
          </p>
          <table className="defo-stats-table">
            <tbody>
              <tr><th>Points parsed and read</th><td>{result.pointCount.toLocaleString()}</td></tr>
              <tr><th>X bounds (mm)</th><td>{Math.round(result.bounds.minX * 1000).toLocaleString()} → {Math.round(result.bounds.maxX * 1000).toLocaleString()}</td></tr>
              <tr><th>Y bounds (mm)</th><td>{Math.round(result.bounds.minY * 1000).toLocaleString()} → {Math.round(result.bounds.maxY * 1000).toLocaleString()}</td></tr>
              <tr><th>Z bounds (mm)</th><td>{Math.round(result.bounds.minZ * 1000).toLocaleString()} → {Math.round(result.bounds.maxZ * 1000).toLocaleString()}</td></tr>
              <tr><th>θ bins</th><td>{result.binsPerCircle} × {result.thetaBinDeg}°</td></tr>
              <tr><th>σ (stdev of R)</th><td>{result.nominal?.stdevRimR_mm != null ? `${Math.round(result.nominal.stdevRimR_mm).toLocaleString()} mm` : '—'}</td></tr>
              <tr><th>Nominal radius (R)</th><td>{result.nominal?.R_mm?.toLocaleString()} mm</td></tr>
              <tr><th>Nominal diameter (D = 2R)</th><td>{result.nominal?.D_mm?.toLocaleString()} mm</td></tr>
              <tr><th>Nominal height (H)</th><td>{result.nominal?.H_mm?.toLocaleString()} mm</td></tr>
              {tilt && (
                <>
                  <tr>
                    <th>Floor normal (raw PCA, pre-untilt)</th>
                    <td>
                      ({tilt.nx.toFixed(4)}, {tilt.ny.toFixed(4)}, {tilt.nz.toFixed(4)})
                    </td>
                  </tr>
                  <tr>
                    <th>Floor tilt</th>
                    <td>{tilt.tiltDeg.toFixed(3)}° from vertical</td>
                  </tr>
                  <tr>
                    <th>Untilt rotations</th>
                    <td>
                      rotX = {tilt.rotXdeg.toFixed(3)}°, rotY = {tilt.rotYdeg.toFixed(3)}°
                      <div style={{ fontSize: '0.85em', opacity: 0.75, marginTop: 4 }}>
                        Right-hand rule: looking from the named positive axis toward the
                        origin, positive values are counter-clockwise rotations.
                        rotX is applied first (zeroes the normal's Y-component),
                        then rotY (zeroes the X-component) — together they bring
                        the floor normal back to +Z.
                      </div>
                    </td>
                  </tr>
                </>
              )}
              {shape && (
                <tr>
                  <th>Floor shape</th>
                  <td>
                    <strong>{shape.classification}</strong>
                    {' '}(linear R² = {shape.linear.r2.toFixed(3)}, quad R² = {shape.quadratic.r2.toFixed(3)})
                  </td>
                </tr>
              )}
              <tr><th>Total time</th><td>{(result.durationMs / 1000).toFixed(2)} s</td></tr>
            </tbody>
          </table>
        </section>
      )}

      {/* Floor shape — placed right after Nominal geometry per spec. */}
      {result && shape && (
        <section className="defo-subcard">
          <h2>Floor shape — detected from untilted cloud</h2>
          <p className="defo-card-hint">
            Per R-bin, the Z values across θ are robust-averaged with a
            ±1.5σ outlier filter (fallback to plain mean when the R-bin
            has fewer than 8 cells). The resulting Z̄(R) profile feeds a
            linear (Z = c<sub>1</sub>R + c<sub>0</sub>) and quadratic
            (Z = c<sub>2</sub>R² + c<sub>1</sub>R + c<sub>0</sub>) least-squares
            fit; the chart compares those models against the raw data.
            Classification:{' '}
            <strong
              onClick={() => {
                setPickerOpen((open) => !open);
                setPendingChoice(shape.classification);
              }}
              style={{
                color: shape.classification === 'flat'      ? '#16a34a' :
                       shape.classification === 'spheroid'  ? '#7c3aed' :
                                                              '#ea580c',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '3px',
              }}
              title="Click to change the floor profile type and recompute downstream views."
            >
              {shape.classification}
            </strong>
            {shape.isOverridden && (
              <span style={{ opacity: 0.7, fontSize: '0.9em', marginLeft: 6 }}>
                (overridden — auto-detected: <em>{shape.autoClassification}</em>)
              </span>
            )}
            . Every (R, Z) cell is plotted as a small blue dot;
            the fitted model is overlaid in red dashed.
          </p>
          {pickerOpen && (
            <div className="defo-shape-picker" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 12px',
              marginBottom: 12,
              background: 'color-mix(in oklch, var(--primary) 8%, var(--bg))',
              border: '1px solid color-mix(in oklch, var(--primary) 30%, var(--border-muted))',
              borderRadius: 6,
              flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: '0.9em', fontWeight: 600, color: 'var(--text)' }}>
                Override profile type:
              </span>
              <select
                value={pendingChoice || shape.classification}
                onChange={(e) => setPendingChoice(e.target.value)}
                style={{
                  width: 140,
                  height: 32,
                  padding: '0 10px',
                  fontSize: '0.9em',
                  borderRadius: 4,
                  border: '1px solid var(--border-muted)',
                  background: 'var(--bg)',
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                }}
              >
                <option value="flat">Flat</option>
                <option value="cone-up">Cone-up</option>
                <option value="cone-down">Cone-down</option>
                <option value="spheroid">Spheroid</option>
              </select>
              {pendingChoice && pendingChoice !== shape.classification && (
                <button
                  type="button"
                  onClick={() => handleRegenerate(pendingChoice)}
                  style={{
                    width: 140,
                    height: 32,
                    padding: 0,
                    fontSize: '0.9em',
                    fontWeight: 600,
                    background: 'var(--primary)',
                    color: 'var(--bg-light)',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Regenerate
                </button>
              )}
              {shape.isOverridden && (
                <button
                  type="button"
                  onClick={() => {
                    setUserClassification(null);
                    setPendingChoice(shape.autoClassification);
                  }}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.85em',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Reset to auto-detected
                </button>
              )}
              <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: 'auto' }}>
                Clicking <strong>Regenerate</strong> reuses the existing
                untilted + binned cloud; only the floor profile and the
                downstream c2m image are recomputed.
              </span>
            </div>
          )}
          <table className="defo-stats-table" style={{ marginBottom: 12 }}>
            <tbody>
              <tr>
                <th>Classification</th>
                <td>
                  {shape.classification}
                  {shape.classification === 'cone-up'   && ' (centre high, rim low — Z falls with R)'}
                  {shape.classification === 'cone-down' && ' (centre low, rim high — Z rises with R)'}
                  {shape.classification === 'spheroid' && ' (curved — flat-ish centre, bends near rim)'}
                  {shape.classification === 'flat' && ' (within ±0.1 % slope)'}
                </td>
              </tr>
              <tr>
                <th>Linear fit</th>
                <td>
                  slope = {(shape.linear.slope_mm_per_mm * 1000).toFixed(3)} mm / m,
                  intercept = {Math.round(shape.linear.intercept_mm).toLocaleString()} mm,
                  R² = {shape.linear.r2.toFixed(4)}
                </td>
              </tr>
              <tr>
                <th>Quadratic fit (global)</th>
                <td>
                  c₂ = {shape.quadratic.c2_per_mm2.toExponential(3)} /mm,
                  c₁ = {shape.quadratic.c1_per_mm.toExponential(3)},
                  c₀ = {Math.round(shape.quadratic.c0_mm).toLocaleString()} mm,
                  R² = {shape.quadratic.r2.toFixed(4)}
                </td>
              </tr>
              {shape.segments && (
                <tr>
                  <th>Piecewise quadratic (1 m segments)</th>
                  <td>
                    {shape.segments.length} segments,
                    R² = {shape.piecewiseR2 != null ? shape.piecewiseR2.toFixed(4) : '—'}
                    {' '}(improvement over global quadratic:
                    {' '}{((shape.piecewiseR2 ?? 0) - shape.quadratic.r2 >= 0 ? '+' : '')}{((shape.piecewiseR2 ?? 0) - shape.quadratic.r2).toFixed(4)})
                  </td>
                </tr>
              )}
              {shape.filterStats && (
                <tr>
                  <th>±1.5σ outlier filter</th>
                  <td>
                    kept {shape.filterStats.cellsKept.toLocaleString()} / {shape.filterStats.cellsAll.toLocaleString()} cells
                    {' '}({shape.filterStats.cellsAll > 0
                      ? (100 * shape.filterStats.cellsKept / shape.filterStats.cellsAll).toFixed(1)
                      : '0'} %),
                    fallback plain mean when an R-bin has &lt; {shape.filterStats.minCountForFilter} cells
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <FloorShapeChart shape={shape} cellsBuffer={result.cellsBuffer} />
          {shape.segments && (
            <p className="defo-card-hint" style={{ marginTop: 10 }}>
              The dashed red curve is built piecewise: every 1 000 mm segment of
              R gets its own least-squares quadratic. Faint vertical dashes mark
              the segment boundaries.
            </p>
          )}
        </section>
      )}

      {/* Local differential settlement — own sub-card, placed
          right after Nominal geometry per spec. */}
      {result && settlementData && (
        <section className="defo-subcard">
          <h2>Local differential settlement (adjacent scanlines)</h2>
          <p className="defo-card-hint">
            Per scanline: Local differential settlement = MAX(the absolute Z difference
            between its rim point and the rim points of its left and right neighbours).
            Allowable Local Differential Settlement = 1 % of arc length between adjacent
            scanlines (<strong>{Math.round(settlementData.localAllowMm).toLocaleString()} mm</strong>).
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="defo-oov-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Scanline (Angle°)</th>
                  <th>Relative height (mm)</th>
                  <th>Absolute height difference to the left scanline (mm)</th>
                  <th>Absolute height difference to the right scanline (mm)</th>
                  <th>Maximum height difference to adjacent scanline (mm)</th>
                  <th>Allowable limit (mm)</th>
                  <th>Percentage deviation from limit (%)</th>
                </tr>
              </thead>
              <tbody>
                {settlementData.localDiff.map((row) => {
                  const cls = row.pct == null ? '' : (row.pct < 80 ? 'ok' : (row.pct <= 90 ? 'warn' : 'fail'));
                  return (
                    <tr key={row.scanlineN}>
                      <td>{row.scanlineN} ({row.angle}°)</td>
                      <td>{row.zRim != null ? Math.round(row.zRim).toLocaleString() : '—'}</td>
                      <td>{row.dLeft  != null ? Math.round(row.dLeft).toLocaleString()  : '—'}</td>
                      <td>{row.dRight != null ? Math.round(row.dRight).toLocaleString() : '—'}</td>
                      <td>{row.dMax   != null ? Math.round(row.dMax).toLocaleString()   : '—'}</td>
                      <td>{Math.round(row.allowable).toLocaleString()}</td>
                      <td className={`defo-oov-pct ${cls}`}>
                        {row.pct != null ? `${row.pct.toFixed(1)} %` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* General differential settlement — own sub-card. */}
      {result && settlementData && (
        <section className="defo-subcard">
          <h2>General differential settlement (any two scanlines)</h2>
          <p className="defo-card-hint">
            The Z range across every scanline's rim. S<sub>max</sub> is the
            allowable: 100 mm for D ≤ 6 m, 300 mm for D ≥ 36 m, otherwise
            (20D + 180) / 3 mm. For D = {Math.round(settlementData.nominalDmm).toLocaleString()} mm
            this gives S<sub>max</sub> = <strong>{Math.round(settlementData.general.sMax).toLocaleString()} mm</strong>.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="defo-oov-table defo-oov-table-all-center" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Z<sub>max</sub> (mm)</th>
                  <th>At scanline(s)</th>
                  <th>Z<sub>min</sub> (mm)</th>
                  <th>At scanline(s)</th>
                  <th>Difference (mm)</th>
                  <th>S<sub>max</sub> (mm)</th>
                  <th>Percentage deviation from limit (%)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{settlementData.general.zMax != null ? Math.round(settlementData.general.zMax).toLocaleString() : '—'}</td>
                  <td>{settlementData.general.maxScanlines.length > 0 ? settlementData.general.maxScanlines.join(', ') : '—'}</td>
                  <td>{settlementData.general.zMin != null ? Math.round(settlementData.general.zMin).toLocaleString() : '—'}</td>
                  <td>{settlementData.general.minScanlines.length > 0 ? settlementData.general.minScanlines.join(', ') : '—'}</td>
                  <td>{settlementData.general.diff != null ? Math.round(settlementData.general.diff).toLocaleString() : '—'}</td>
                  <td>{Math.round(settlementData.general.sMax).toLocaleString()}</td>
                  <td className={`defo-oov-pct ${settlementData.general.pct == null ? '' : (settlementData.general.pct < 80 ? 'ok' : (settlementData.general.pct <= 90 ? 'warn' : 'fail'))}`}>
                    {settlementData.general.pct != null ? `${settlementData.general.pct.toFixed(1)} %` : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Edge settlement — own sub-card. Always rendered so the user
          can see what shape gated the result. Skipped for
          spheroid / cone-down (with an explanatory note); for cone-up
          only valid when the fitted gradient is ≤ 0.05 (5 % slope),
          steeper slopes show the same kind of note. */}
      {result && settlementData && (() => {
        const shapeClass = (shape?.classification || '').toLowerCase();
        if (shapeClass === 'spheroid' || shapeClass === 'cone-down') {
          return (
            <section className="defo-subcard">
              <h2>Edge settlement (per scanline)</h2>
              <p className="defo-card-hint">
                Edge settlement is not computed — EEMUA 159 does not define
                a rim-band edge-settlement check for{' '}
                <strong>{shapeClass}</strong> floors.
              </p>
            </section>
          );
        }
        if (shapeClass === 'cone-up') {
          const slope = Math.abs(shape?.linear?.c1 ?? shape?.coefficients?.c1 ?? 0);
          if (slope > 0.05) {
            return (
              <section className="defo-subcard">
                <h2>Edge settlement (per scanline)</h2>
                <p className="defo-card-hint">
                  Edge settlement is not computed — fitted <strong>cone-up</strong>{' '}
                  slope ({(slope * 100).toFixed(2)} %) exceeds the 5 % threshold
                  above which the rim-band check is no longer meaningful.
                </p>
              </section>
            );
          }
        }
        return (
          <section className="defo-subcard">
            <h2>Edge settlement (per scanline)</h2>
            <p className="defo-card-hint">
              For each scanline, take the 750 mm band inboard from its actual
              rim R (so per-scanline rim variation is preserved). Within the
              band find Z<sub>max</sub> and the R where it occurs.
              <br />
              <strong>Breakpoint distance</strong>: 750 mm if Z<sub>max</sub>{' '}
              sits at the rim or at the inner edge of the band; otherwise
              (R<sub>rim</sub> − R @ Z<sub>max</sub>).
              <br />
              <strong>Allowable limit = breakpoint distance / 6.</strong>{' '}
              <strong>Measured</strong> = |Z @ rim − Z<sub>max</sub>|, except
              when Z<sub>max</sub> coincides with the rim — then |Z @ rim − Z @ (rim − 750 mm)|.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="defo-oov-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Scanline (Angle°)</th>
                    <th>Breakpoint distance (mm)</th>
                    <th>Allowable limit (mm)</th>
                    <th>Measured edge settlement (mm)</th>
                    <th>Percentage deviation from limit (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {settlementData.edgeSettlement.map((row) => {
                    const cls = row.pct == null ? '' : (row.pct < 80 ? 'ok' : (row.pct <= 90 ? 'warn' : 'fail'));
                    return (
                      <tr key={row.scanlineN}>
                        <td>{row.scanlineN} ({row.angle}°)</td>
                        <td>{row.breakpoint != null ? Math.round(row.breakpoint).toLocaleString() : '—'}</td>
                        <td>{row.allowable  != null ? Math.round(row.allowable).toLocaleString()  : '—'}</td>
                        <td>{row.measured   != null ? Math.round(row.measured).toLocaleString()   : '—'}</td>
                        <td className={`defo-oov-pct ${cls}`}>
                          {row.pct != null ? `${row.pct.toFixed(1)} %` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })()}

      {result && scanlineCount >= 2 && (
        <section className="defo-subcard">
          <h2>Scanline Floor profiles</h2>
          <p className="defo-card-hint">
            One chart per opposite-pair, drawn from the untilted floor cloud.
            Each chart shows the height at the scanline's θ-bin against radius,
            with Z values averaged into R-slabs of 100 mm.
          </p>
          <div className="defo-chart-grid" ref={scanlineGridRef}>
            {Array.from({ length: scanlineCount / 2 }, (_, i) => i).map((i) => (
              <ScanlinePairChart
                key={i}
                pairIndex={i}
                totalScanlines={scanlineCount}
                angleA={angles[i]}
                angleB={angles[i + scanlineCount / 2]}
                pointsByBin={slabbedByBin}
                binsPerCircle={result.binsPerCircle}
                thetaBinDeg={result.thetaBinDeg}
                shape={shape}
              />
            ))}
          </div>
        </section>
      )}

      {result && shape && c2mStats && (
        <section className="defo-subcard">
          <h2>Un-tilted Floor Deformation Heatmap</h2>
          <div className="defo-unrolled-canvas-wrap">
            <FloorC2MCanvas
              cellsBuffer={result.cellsBuffer}
              shape={shape}
              nominalRMm={result.nominal.R_mm}
              thetaBinDeg={result.thetaBinDeg}
              cMin={c2mStats.cMin}
              cMax={c2mStats.cMax}
              histogram={histogramData}
              angles={angles}
              canvasRef={c2mCanvasRef}
            />
          </div>
        </section>
      )}

      {result && c2mStats && shape && file && (
        <section className="defo-subcard">
          <h2>Download 3D Model (.txt ASCII)</h2>
          <p className="defo-card-hint">
            A full-density coloured point cloud of the un-tilted floor (PCA tilt
            removed), each point coloured exactly by its C2M cell. Tab-delimited
            X Y Z R G B, coordinates in metres to 3 decimal precision and colors
            ranging from 0 to 255. Open the .txt ASCII file in CloudCompare and
            export as .las file or other 3D point cloud formats.
          </p>
          <button
            className="btn btn-primary defo-run-btn"
            onClick={handleDownloadModel}
            disabled={exporting}
          >
            {exporting ? 'Generating…' : 'Download .txt'}
          </button>
        </section>
      )}

      </section>
      )}

      {/* ── Progress modal — blocks page during the worker run and
            flashes briefly on scanline-angle commits. ─────────── */}
      <ProgressModal
        open={status === 'running' || recompute || regenerating}
        title={
          regenerating ? 'Regenerating floor profile…' :
          recompute && status !== 'running' ? 'Rebuilding scanline outputs…' :
          'Running deformation…'
        }
        progress={
          status === 'running' ? progress :
          regenerating ? { stage: 'Regenerating…', pct: 100 } :
          { stage: 'Rebuilding…', pct: 100 }
        }
        progressMsgs={status === 'running' ? progressMsgs : []}
        error={status === 'running' ? error : ''}
      />
    </div>
  );
}
